"use client";

import React, {
  ChangeEvent,
  HTMLAttributes,
  forwardRef,
  useCallback,
  useRef
} from "react";
import {
  doLocalUpload,
  doUpload
} from "@/features/settings/components/upload/utils";

type FileInputProps = HTMLAttributes<HTMLInputElement> & {
  onFile: (
    file: File | undefined,
    event: ChangeEvent<HTMLInputElement>
  ) => Promise<void>;
  accept: string;
};

const FileInput = forwardRef<HTMLInputElement, FileInputProps>(
  ({ onFile = () => {}, ...restOfProps }, forwardedRef) => {
    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>): void => {
        const file = event.target?.files?.[0];
        onFile(file, event);
      },
      [onFile]
    );

    return (
      <input
        onChange={handleChange}
        {...restOfProps}
        ref={forwardedRef}
        type="file"
      />
    );
  }
);

export const useS3Upload = (apiEndPoint: string, local = false) => {
  const ref = useRef<HTMLInputElement>(null);
  const openFileDialog = useCallback(() => {
    if (ref.current) {
      ref.current.value = "";
      ref.current?.click();
    }
  }, [ref]);

  const uploadToS3 = useCallback(
    async (file: File, queryString: { [idx: string]: string } = {}) => {
      const fn = local ? doLocalUpload : doUpload;
      const data = await fn(file, apiEndPoint, queryString);
      return {
        key: data.key,
        url: `https://cdn.gettactic.com/${data.key}`
      };
    },
    [local, apiEndPoint]
  );

  const FileInputMemo = useCallback(
    (props: FileInputProps) => (
      <FileInput {...props} ref={ref} style={{ display: "none" }} />
    ),
    [ref]
  );

  return {
    FileInput: FileInputMemo,
    openFileDialog,
    uploadToS3
  };
};
